import React, { useState, useEffect, useCallback, useRef, memo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Memoized component to prevent re-renders
const TopPerformers = memo(function TopPerformers() {
  const [tracks, setTracks] = useState([]);
  const [raceDate, setRaceDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('yesterday'); // 'yesterday' | 'today'
  const [isHidden, setIsHidden] = useState(() => {
    return localStorage.getItem('hideTopPerformers') === 'true';
  });
  const [isPaused, setIsPaused] = useState(false);

  const scrollRef = useRef(null);
  const positionRef = useRef(0);
  const animationRef = useRef(null);

  const fetchTrackSummary = useCallback(async () => {
    try {
      // The admin sets a single global view (yesterday/today) that is
      // authoritative for everyone; resolve the actual date server-side (EST).
      let v = 'yesterday';
      let dateStr = null;
      const sres = await fetch(`${API_URL}/api/quickpick/ticker-setting`);
      if (sres.ok) {
        const s = await sres.json();
        v = s.view || 'yesterday';
        dateStr = s.race_date || null;
      }
      if (!dateStr) {
        const d = new Date();
        if (v === 'yesterday') d.setDate(d.getDate() - 1);
        dateStr = d.toISOString().split('T')[0];
      }
      setView(v);

      const response = await fetch(`${API_URL}/api/quickpick/track-summary?race_date=${dateStr}`);
      if (response.ok) {
        const data = await response.json();
        setTracks(data.tracks || []);
        setRaceDate(data.race_date || dateStr);
      }
    } catch (error) {
      console.error('Failed to fetch track summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Always fetch once on mount to learn the admin's global view (yesterday/today)
  // and load the data.
  useEffect(() => {
    fetchTrackSummary();
  }, [fetchTrackSummary]);

  // Only auto-refresh when showing TODAY. Today's results are processed
  // server-side every 30 min, so a 3-min poll keeps the ticker current.
  // YESTERDAY's results are final/static — no polling at all (avoids needless
  // /ticker-setting + /track-summary calls).
  useEffect(() => {
    if (view !== 'today') return;
    const id = setInterval(fetchTrackSummary, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [view, fetchTrackSummary]);

  // JavaScript-based animation using requestAnimationFrame
  useEffect(() => {
    if (!scrollRef.current || tracks.length < 4 || isHidden || isPaused) {
      return;
    }

    const container = scrollRef.current;
    const contentWidth = container.scrollWidth / 2; // Half because content is duplicated
    const speed = 50; // pixels per second
    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      positionRef.current += speed * deltaTime;

      // Reset when we've scrolled past the first set of items
      if (positionRef.current >= contentWidth) {
        positionRef.current = 0;
      }

      container.style.transform = `translateX(-${positionRef.current}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [tracks.length, isHidden, isPaused]);

  const toggleHidden = () => {
    const newValue = !isHidden;
    setIsHidden(newValue);
    localStorage.setItem('hideTopPerformers', newValue.toString());
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Hide the whole bar only on the default (yesterday) view when there's no data.
  // On "today" keep it visible so the toggle stays accessible while results fill in.
  if (loading && tracks.length === 0 && view === 'yesterday') {
    return null;
  }
  if (tracks.length === 0 && view === 'yesterday') {
    return null;
  }

  const shouldScroll = tracks.length >= 4;

  return (
    <div className="mt-4" data-testid="top-performers-ticker">
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="flex items-center px-4 py-2.5">
          {/* Left: Label + view toggle */}
          <div className="flex items-center gap-2 shrink-0">
            <h3 className="text-amber-400 text-sm font-semibold whitespace-nowrap">
              AutoGen Exacta Results<span className="text-amber-300 text-base font-bold">*</span>
            </h3>
            {raceDate && (
              <span className="text-amber-300/70 text-sm font-medium">
                ({formatDisplayDate(raceDate)} · {view === 'today' ? 'Today' : 'Yesterday'})
              </span>
            )}
          </div>

          {/* Middle: Ticker or empty-today message */}
          {!isHidden && tracks.length > 0 && (
            <div
              className="flex-1 mx-6 overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div
                ref={scrollRef}
                className={`flex gap-6 ${shouldScroll ? '' : 'justify-start'}`}
                style={{ willChange: 'transform' }}
              >
                {/* Render items twice for seamless loop when scrolling */}
                {(shouldScroll ? [...tracks, ...tracks] : tracks).map((t, idx) => (
                  <div
                    key={`${t.track}-${idx}`}
                    className="flex items-center gap-2 whitespace-nowrap"
                  >
                    <span className="text-white font-semibold text-sm">
                      {t.track}
                    </span>
                    <span className="text-slate-400 text-sm">
                      {t.races} Races
                    </span>
                    <span className="text-slate-400 text-sm">
                      {t.wins} {t.wins === 1 ? 'Win' : 'Wins'}
                    </span>
                    <span className={`font-bold text-sm ${t.roi >= 1 ? 'text-emerald-400' : t.roi >= 0.9 ? 'text-orange-400' : 'text-red-400'}`}>
                      ROI {t.roi.toFixed(2)}
                    </span>
                    {idx < (shouldScroll ? tracks.length * 2 - 1 : tracks.length - 1) && (
                      <span className="text-slate-600 ml-2">|</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isHidden && tracks.length === 0 && (
            <div className="flex-1 mx-6 overflow-hidden" data-testid="ticker-empty-today">
              <span className="text-slate-500 text-sm">
                No completed races yet today — updating automatically as results come in.
              </span>
            </div>
          )}

          {/* Right: Hide/Show */}
          <button
            onClick={toggleHidden}
            className="text-slate-500 hover:text-slate-300 text-xs transition shrink-0 ml-auto"
          >
            {isHidden ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>

      {/* Footnote */}
      <div className="text-right mt-1">
        <span className="text-amber-400 text-sm font-semibold">*$100 Base Wager</span>
      </div>
    </div>
  );
});

TopPerformers.displayName = 'TopPerformers';

export default TopPerformers;
